
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// ── Data model ──────────────────────────────────────────────────────────────

interface AttributeOption {
  id: string
  name: string
  weight: number
  promptFragment: string
  description?: string
}

interface AttributeCategory {
  id: string
  name: string
  sortOrder: number
  isRequired: boolean
  noneWeight: number
  options: AttributeOption[]
}

interface ConflictRule {
  id: string
  enabled: boolean
  condition: { categoryId: string; optionId: string }
  exclusion: { categoryId: string; optionId: string }
}

// ── Props ───────────────────────────────────────────────────────────────────

interface DataTableProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function makeOption(name = '', weight = 10): AttributeOption {
  return { id: uid(), name, weight, promptFragment: '' }
}

function makeCategory(name = 'New Category'): AttributeCategory {
  return {
    id: uid(),
    name,
    sortOrder: 0,
    isRequired: true,
    noneWeight: 0,
    options: [makeOption('Option 1'), makeOption('Option 2')],
  }
}

function totalOptions(cats: AttributeCategory[]) {
  return cats.reduce((sum, c) => sum + c.options.length, 0)
}

// ── Import/Export helpers ───────────────────────────────────────────────────

function categoriesToJson(cats: AttributeCategory[]): string {
  return JSON.stringify(cats, null, 2)
}

function categoriesToCsv(cats: AttributeCategory[]): string {
  const lines = ['Category,Option,Weight,Prompt Fragment']
  for (const cat of cats) {
    for (const opt of cat.options) {
      const frag = opt.promptFragment.includes(',') ? `"${opt.promptFragment}"` : opt.promptFragment
      const name = opt.name.includes(',') ? `"${opt.name}"` : opt.name
      const catName = cat.name.includes(',') ? `"${cat.name}"` : cat.name
      lines.push(`${catName},${name},${opt.weight},${frag}`)
    }
  }
  return lines.join('\n')
}

function parseCsv(text: string): AttributeCategory[] {
  const lines = text.trim().split('\n')
  // Skip header if present
  const startIdx = lines[0]?.toLowerCase().includes('category') ? 1 : 0
  const catMap = new Map<string, AttributeCategory>()

  for (let i = startIdx; i < lines.length; i++) {
    const parts = parseCsvLine(lines[i])
    if (parts.length < 2) continue
    const [catName, optName, weightStr, fragment] = parts
    if (!catName || !optName) continue

    if (!catMap.has(catName)) {
      catMap.set(catName, {
        id: uid(),
        name: catName,
        sortOrder: catMap.size,
        isRequired: true,
        noneWeight: 0,
        options: [],
      })
    }
    catMap.get(catName)!.options.push(makeOption(optName, Number(weightStr) || 10))
    if (fragment) {
      const opts = catMap.get(catName)!.options
      opts[opts.length - 1].promptFragment = fragment
    }
  }
  return Array.from(catMap.values())
}

/** Simple CSV line parser that handles quoted fields */
function parseCsvLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if ((ch === ',' || ch === '\t') && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current.trim())
  return result
}

function parseJson(text: string): AttributeCategory[] {
  const data = JSON.parse(text)
  if (!Array.isArray(data)) return []
  return data.map((cat: Record<string, unknown>, i: number) => ({
    id: uid(),
    name: String(cat.name || `Category ${i + 1}`),
    sortOrder: Number(cat.sortOrder ?? i),
    isRequired: Boolean(cat.isRequired ?? true),
    noneWeight: Number(cat.noneWeight ?? 0),
    options: Array.isArray(cat.options)
      ? (cat.options as Record<string, unknown>[]).map((o) => ({
          id: uid(),
          name: String(o.name || ''),
          weight: Number(o.weight ?? 10),
          promptFragment: String(o.promptFragment || ''),
        }))
      : [],
  }))
}

function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ── Weight bar ──────────────────────────────────────────────────────────────

function WeightBar({ options }: { options: AttributeOption[] }) {
  const total = options.reduce((s, o) => s + o.weight, 0)
  if (total === 0) return null

  const colors = [
    'bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-pink-500', 'bg-lime-500',
    'bg-indigo-500', 'bg-orange-500',
  ]

  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
      {options.map((opt, i) => (
        <div
          key={opt.id}
          className={cn('h-full transition-all', colors[i % colors.length])}
          style={{ width: `${(opt.weight / total) * 100}%` }}
          title={`${opt.name}: ${Math.round((opt.weight / total) * 100)}%`}
        />
      ))}
    </div>
  )
}

// ── Option row ──────────────────────────────────────────────────────────────

interface OptionRowProps {
  option: AttributeOption
  totalWeight: number
  onUpdate: (id: string, updates: Partial<AttributeOption>) => void
  onRemove: (id: string) => void
}

function OptionRow({ option, totalWeight, onUpdate, onRemove }: OptionRowProps) {
  const pct = totalWeight > 0 ? Math.round((option.weight / totalWeight) * 100) : 0

  return (
    <div className="group pl-6 pr-1 py-0.5">
      <div className="flex items-center gap-1.5">
        <Input
          value={option.name}
          onChange={(e) => onUpdate(option.id, { name: e.target.value })}
          placeholder="Option name"
          className="h-6 flex-1 border-0 bg-transparent px-1 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="flex items-center gap-1 shrink-0">
          <Input
            type="number"
            value={option.weight}
            onChange={(e) => onUpdate(option.id, { weight: Math.max(0, Number(e.target.value) || 0) })}
            className="h-6 w-12 border-0 bg-transparent px-1 text-xs text-right tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0"
            min={0}
          />
          <span className="text-[10px] text-muted-foreground w-8 text-right tabular-nums">{pct}%</span>
        </div>
        <button
          onClick={() => onRemove(option.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
        >
          <Icon name="x" size="sm" />
        </button>
      </div>
      <Input
        value={option.promptFragment}
        onChange={(e) => onUpdate(option.id, { promptFragment: e.target.value })}
        placeholder="Prompt fragment..."
        className="h-5 ml-1 mt-0.5 border-0 bg-transparent px-1 text-[10px] text-muted-foreground italic focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
      />
      <Input
        value={option.description ?? ''}
        onChange={(e) => onUpdate(option.id, { description: e.target.value })}
        placeholder="Description (notes)..."
        className="h-5 ml-1 border-0 bg-transparent px-1 text-[10px] text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/30"
      />
    </div>
  )
}

// ── Category row ────────────────────────────────────────────────────────────

interface CategoryRowProps {
  category: AttributeCategory
  expanded: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onToggle: () => void
  onUpdate: (updates: Partial<AttributeCategory>) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onUpdateOption: (optId: string, updates: Partial<AttributeOption>) => void
  onRemoveOption: (optId: string) => void
  onAddOption: () => void
}

const CategoryRow = React.memo(function CategoryRow({
  category,
  expanded,
  canMoveUp,
  canMoveDown,
  onToggle,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onUpdateOption,
  onRemoveOption,
  onAddOption,
}: CategoryRowProps) {
  const totalWeight = category.options.reduce((s, o) => s + o.weight, 0)

  return (
    <div className="border-b border-border/50 last:border-0">
      {/* Category header */}
      <div className="group flex items-center gap-1.5 px-2 py-1.5 cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onMoveUp() }}
            disabled={!canMoveUp}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none"
          >
            <Icon name="chevron-up" size="sm" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMoveDown() }}
            disabled={!canMoveDown}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none"
          >
            <Icon name="chevron-down" size="sm" />
          </button>
        </div>
        <Icon
          name={expanded ? 'chevron-down' : 'chevron-right'}
          size="sm"
          className="text-muted-foreground shrink-0"
        />
        <Input
          value={category.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          className="h-6 flex-1 border-0 bg-transparent px-1 text-xs font-medium focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <button
          onClick={(e) => {
            e.stopPropagation()
            onUpdate({ isRequired: !category.isRequired, noneWeight: category.isRequired ? 50 : 0 })
          }}
          className={cn(
            'text-[9px] shrink-0 px-1 rounded',
            category.isRequired ? 'text-muted-foreground/50' : 'text-amber-500 bg-amber-500/10'
          )}
          title={category.isRequired ? 'Required — click to make optional' : 'Optional — click to make required'}
        >
          {category.isRequired ? 'req' : 'opt'}
        </button>
        <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
          {category.options.length} opt
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
        >
          <Icon name="x" size="sm" />
        </button>
      </div>

      {/* Weight bar + normalize warning */}
      <div className="px-2 pb-1">
        <WeightBar options={category.options} />
        {totalWeight > 0 && totalWeight !== 100 && (
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[9px] text-amber-500">
              Weights sum to {totalWeight}, not 100
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const factor = 100 / totalWeight
                category.options.forEach((opt) => {
                  onUpdateOption(opt.id, { weight: Math.round(opt.weight * factor) })
                })
              }}
              className="text-[9px] text-amber-500 hover:text-amber-400 underline"
            >
              Normalize
            </button>
          </div>
        )}
      </div>

      {/* Expanded: option list */}
      {expanded && (
        <div className="pb-1.5">
          {category.options.map((opt) => (
            <OptionRow
              key={opt.id}
              option={opt}
              totalWeight={totalWeight}
              onUpdate={onUpdateOption}
              onRemove={onRemoveOption}
            />
          ))}
          <button
            onClick={onAddOption}
            className="flex items-center gap-1 pl-6 py-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="plus" size="sm" />
            Add option
          </button>
        </div>
      )}
    </div>
  )
}, (prev, next) => {
  return (
    prev.category === next.category &&
    prev.expanded === next.expanded &&
    prev.canMoveUp === next.canMoveUp &&
    prev.canMoveDown === next.canMoveDown
  )
})

// ── Conflict rule row ───────────────────────────────────────────────────────

interface ConflictRuleRowProps {
  rule: ConflictRule
  categories: AttributeCategory[]
  onUpdate: (id: string, updates: Partial<ConflictRule>) => void
  onRemove: (id: string) => void
}

function ConflictRuleRow({ rule, categories, onUpdate, onRemove }: ConflictRuleRowProps) {
  const condCat = categories.find((c) => c.id === rule.condition.categoryId)
  const exclCat = categories.find((c) => c.id === rule.exclusion.categoryId)

  return (
    <div className="group flex items-start gap-1 px-2 py-1 text-[10px]">
      <button
        onClick={() => onUpdate(rule.id, { enabled: !rule.enabled })}
        className={cn('mt-0.5 shrink-0', rule.enabled ? 'text-foreground' : 'text-muted-foreground/40')}
      >
        <Icon name={rule.enabled ? 'check-square' : 'square'} size="sm" />
      </button>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-muted-foreground">IF</span>
          <select
            value={rule.condition.categoryId}
            onChange={(e) => onUpdate(rule.id, { condition: { ...rule.condition, categoryId: e.target.value, optionId: '' } })}
            className="h-5 bg-muted rounded px-1 text-[10px] border-0 outline-none nodrag nopan"
          >
            <option value="">Category...</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="text-muted-foreground">=</span>
          <select
            value={rule.condition.optionId}
            onChange={(e) => onUpdate(rule.id, { condition: { ...rule.condition, optionId: e.target.value } })}
            className="h-5 bg-muted rounded px-1 text-[10px] border-0 outline-none nodrag nopan"
          >
            <option value="">Option...</option>
            {condCat?.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-muted-foreground">THEN</span>
          <select
            value={rule.exclusion.categoryId}
            onChange={(e) => onUpdate(rule.id, { exclusion: { ...rule.exclusion, categoryId: e.target.value, optionId: '' } })}
            className="h-5 bg-muted rounded px-1 text-[10px] border-0 outline-none nodrag nopan"
          >
            <option value="">Category...</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="text-destructive">≠</span>
          <select
            value={rule.exclusion.optionId}
            onChange={(e) => onUpdate(rule.id, { exclusion: { ...rule.exclusion, optionId: e.target.value } })}
            className="h-5 bg-muted rounded px-1 text-[10px] border-0 outline-none nodrag nopan"
          >
            <option value="">Option...</option>
            {exclCat?.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </div>
      <button
        onClick={() => onRemove(rule.id)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0 mt-0.5"
      >
        <Icon name="x" size="sm" />
      </button>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

function DataTableComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onSelect,
  onHoverChange,
  onStateChange,
  state = {},
}: DataTableProps) {
  // Hydrate from persisted state
  const [categories, setCategories] = useState<AttributeCategory[]>(() => {
    const saved = state.categories as AttributeCategory[] | undefined
    return saved?.length ? saved : [makeCategory('Category 1')]
  })
  const [conflictRules, setConflictRules] = useState<ConflictRule[]>(() => {
    return (state.conflictRules as ConflictRule[]) ?? []
  })
  const [activeTab, setActiveTab] = useState<'data' | 'conflicts'>('data')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Persist changes to node state
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    onStateChange?.((prev) => ({ ...prev, categories, conflictRules }))
  }, [categories, conflictRules, onStateChange])

  // ── Category CRUD ───────────────────────────────────────────────────────

  const addCategory = useCallback(() => {
    setCategories((prev) => {
      const cat = makeCategory(`Category ${prev.length + 1}`)
      return [...prev, cat]
    })
  }, [])

  const updateCategory = useCallback((catId: string, updates: Partial<AttributeCategory>) => {
    setCategories((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, ...updates } : c))
    )
  }, [])

  const removeCategory = useCallback((catId: string) => {
    setCategories((prev) => prev.filter((c) => c.id !== catId))
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.delete(catId)
      return next
    })
  }, [])

  // ── Option CRUD ─────────────────────────────────────────────────────────

  const updateOption = useCallback((catId: string, optId: string, updates: Partial<AttributeOption>) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? { ...c, options: c.options.map((o) => (o.id === optId ? { ...o, ...updates } : o)) }
          : c
      )
    )
  }, [])

  const removeOption = useCallback((catId: string, optId: string) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId ? { ...c, options: c.options.filter((o) => o.id !== optId) } : c
      )
    )
  }, [])

  const addOption = useCallback((catId: string) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? { ...c, options: [...c.options, makeOption(`Option ${c.options.length + 1}`)] }
          : c
      )
    )
  }, [])

  // ── Conflict rule CRUD ───────────────────────────────────────────────────

  const addConflictRule = useCallback(() => {
    setConflictRules((prev) => [
      ...prev,
      {
        id: uid(),
        enabled: true,
        condition: { categoryId: '', optionId: '' },
        exclusion: { categoryId: '', optionId: '' },
      },
    ])
  }, [])

  const updateConflictRule = useCallback((ruleId: string, updates: Partial<ConflictRule>) => {
    setConflictRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, ...updates } : r))
    )
  }, [])

  const removeConflictRule = useCallback((ruleId: string) => {
    setConflictRules((prev) => prev.filter((r) => r.id !== ruleId))
  }, [])

  // ── Category reorder ─────────────────────────────────────────────────────

  const moveCategory = useCallback((catId: string, direction: -1 | 1) => {
    setCategories((prev) => {
      const idx = prev.findIndex((c) => c.id === catId)
      if (idx < 0) return prev
      const newIdx = idx + direction
      if (newIdx < 0 || newIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[newIdx]] = [next[newIdx], next[idx]]
      return next
    })
  }, [])

  // ── Import/Export ────────────────────────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string
      try {
        const parsed = file.name.endsWith('.json') ? parseJson(text) : parseCsv(text)
        if (parsed.length > 0) setCategories(parsed)
      } catch {
        // silently ignore invalid files
      }
    }
    reader.readAsText(file)
    // Reset so same file can be re-imported
    e.target.value = ''
  }, [])

  const handleExportJson = useCallback(() => {
    downloadFile(categoriesToJson(categories), 'data-table.json', 'application/json')
  }, [categories])

  const handleExportCsv = useCallback(() => {
    downloadFile(categoriesToCsv(categories), 'data-table.csv', 'text/csv')
  }, [categories])

  // ── Toggle expand ───────────────────────────────────────────────────────

  const toggleExpand = useCallback((catId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(catId)) next.delete(catId)
      else next.add(catId)
      return next
    })
  }, [])

  // ── Wheel scroll isolation ──────────────────────────────────────────────

  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight
      const canScrollUp = el.scrollTop > 0
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        e.stopPropagation()
      }
    }
    el.addEventListener('wheel', handleWheel)
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Data Table'}
      icon="table"
      selected={selected}
      nodeType="text"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="relative flex flex-col flex-1 p-0 overflow-hidden">
        {/* Summary header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {categories.length} categories · {totalOptions(categories)} options
            {conflictRules.length > 0 && ` · ${conflictRules.length} rules`}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border/50">
          <button
            onClick={() => setActiveTab('data')}
            className={cn(
              'flex-1 py-1 text-[10px] font-medium transition-colors',
              activeTab === 'data' ? 'text-foreground border-b-2 border-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Data
          </button>
          <button
            onClick={() => setActiveTab('conflicts')}
            className={cn(
              'flex-1 py-1 text-[10px] font-medium transition-colors',
              activeTab === 'conflicts' ? 'text-foreground border-b-2 border-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Conflicts{conflictRules.length > 0 ? ` (${conflictRules.length})` : ''}
          </button>
        </div>

        {activeTab === 'data' ? (
          <>
            {/* Category list */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto scrollbar-thin"
              style={{ maxHeight: '340px' }}
            >
              {categories.map((cat, idx) => (
                <CategoryRow
                  key={cat.id}
                  category={cat}
                  expanded={expandedIds.has(cat.id)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < categories.length - 1}
                  onToggle={() => toggleExpand(cat.id)}
                  onUpdate={(updates) => updateCategory(cat.id, updates)}
                  onRemove={() => removeCategory(cat.id)}
                  onMoveUp={() => moveCategory(cat.id, -1)}
                  onMoveDown={() => moveCategory(cat.id, 1)}
                  onUpdateOption={(optId, updates) => updateOption(cat.id, optId, updates)}
                  onRemoveOption={(optId) => removeOption(cat.id, optId)}
                  onAddOption={() => addOption(cat.id)}
                />
              ))}
            </div>

            {/* Footer: Add + Import/Export */}
            <div className="px-2 py-1.5 border-t border-border/50 flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={addCategory}
                className="h-6 flex-1 text-xs text-muted-foreground hover:text-foreground gap-1"
              >
                <Icon name="plus" size="sm" />
                Add
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                title="Import CSV/JSON"
              >
                <Icon name="upload" size="sm" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportJson}
                className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                title="Export JSON"
              >
                JSON
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportCsv}
                className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                title="Export CSV"
              >
                CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json,.tsv,.txt"
                onChange={handleImport}
                className="hidden"
              />
            </div>
          </>
        ) : (
          <>
            {/* Conflict rules list */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto scrollbar-thin"
              style={{ maxHeight: '340px' }}
            >
              {conflictRules.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                  No conflict rules yet
                </div>
              ) : (
                conflictRules.map((rule) => (
                  <ConflictRuleRow
                    key={rule.id}
                    rule={rule}
                    categories={categories}
                    onUpdate={updateConflictRule}
                    onRemove={removeConflictRule}
                  />
                ))
              )}
            </div>

            {/* Add rule button */}
            <div className="px-2 py-1.5 border-t border-border/50">
              <Button
                variant="ghost"
                size="sm"
                onClick={addConflictRule}
                className="h-6 w-full text-xs text-muted-foreground hover:text-foreground gap-1"
              >
                <Icon name="plus" size="sm" />
                Add rule
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </NodeContainer>
  )
}

export const DataTable = withNodeOptimization(DataTableComponent, {
  compareProps: ['id', 'title', 'selected', 'state'],
})
